import type { RecentDocumentListDtoOutputItemsItem } from '@batiflow/api-client';
import { useRecentDocumentsControllerList } from '@batiflow/api-client';
import { formatRelative } from '@batiflow/i18n';
import { EmptyState, SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Document24Regular, DocumentMultiple24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { AppLink } from '@/shared/components/app-link';
import { listStyles } from './list-styles';

type RecentFile = RecentDocumentListDtoOutputItemsItem;

/** The Documents tab of a shared space (a route segment, not text). */
const SPACE_DOCUMENTS_TAB = 'documents';

const useStyles = __css({"link":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","Dbcxam":0,"rjzwhg":0,"Bblux5w":"fnllxr2","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","sj55zd":"f19n0e5","Bi91k9c":"feu1g3u","Jwef8y":"f1knas48","Bde5pd6":"fbnuktb"},"icon":{"Bnnss6s":"fi64zpg","sj55zd":"f16muhyy","mc9l5x":"f22iagw"},"body":{"Bh6795r":"fqerorx","Bgto7am":"fuzgk9b","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"title":{"Bhrd7zp":"fl43uef","Btd35i7":"fjksvth"},"meta":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"fsnqrgy","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"},"messages":{"D305fb":"fj1pxrr"}});

/** Where a recent file opens: the viewer of its project, its shared space, or the Organisation tab. */
function FileLink({
  orgId,
  file,
  className,
  children,
}: {
  orgId: string;
  file: RecentFile;
  className: string;
  children: React.ReactNode;
}) {
  if (file.projectId)
    return (
      <AppLink
        to="/o/$orgId/projects/$projectId/documents/$documentId"
        params={{ orgId, projectId: file.projectId, documentId: file.id }}
        className={className}
      >
        {children}
      </AppLink>
    );
  if (file.spaceKind === 'organization')
    return (
      <AppLink
        to="/o/$orgId/files"
        params={{ orgId }}
        search={{ tab: 'organisation' }}
        className={className}
      >
        {children}
      </AppLink>
    );
  return (
    <AppLink
      to="/s/$spaceId/$tab"
      params={{ spaceId: file.spaceId, tab: SPACE_DOCUMENTS_TAB }}
      className={className}
    >
      {children}
    </AppLink>
  );
}

/**
 * Recent files (GED-FE-09): the latest changed documents the person can read in the organisation,
 * newest first; the API already leaves out what they cannot open.
 */
export function RecentFiles({ orgId }: { orgId: string }) {
  const { t } = useTranslation('documents');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const list = listStyles();
  const locale = useLocale();
  const recent = useRecentDocumentsControllerList(orgId, { limit: 30 });

  if (recent.isError && !recent.data)
    return (
      <MessageBar className={styles.messages} intent="error" layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>{t('filesApp.recent.loadFailedTitle')}</MessageBarTitle>
          {t('filesApp.recent.loadFailedBody')}
        </MessageBarBody>
        <MessageBarActions>
          <Button
            onClick={() => {
              void recent.refetch();
            }}
          >
            {tCommon('actions.retry')}
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  if (recent.isPending) return <SkeletonList count={5} />;
  const items = recent.data.items;
  if (items.length === 0)
    return (
      <EmptyState
        illustration={<DocumentMultiple24Regular />}
        title={t('filesApp.recent.emptyTitle')}
        description={t('filesApp.recent.emptyBody')}
      />
    );
  const now = new Date();
  return (
    <ul className={list.list} aria-label={t('filesApp.recent.listLabel')}>
      {items.map((file) => (
        <li key={file.id}>
          <FileLink orgId={orgId} file={file} className={styles.link}>
            <span className={styles.icon} aria-hidden="true">
              <Document24Regular />
            </span>
            <span className={styles.body}>
              <bdi className={styles.title}>{file.title}</bdi>
              <span className={styles.meta}>
                {file.code ? (
                  <>
                    <span dir="ltr">{file.code}</span>
                    <span aria-hidden="true">·</span>
                  </>
                ) : null}
                <bdi>{file.projectCode ? isolate(file.projectCode) : file.spaceName}</bdi>
                <span aria-hidden="true">·</span>
                <span>{t('filesApp.recent.inFolder', { folder: isolate(file.folderName) })}</span>
                <span aria-hidden="true">·</span>
                <span>
                  {t('filesApp.recent.updated', {
                    when: formatRelative(file.updatedAt, now, locale),
                  })}
                </span>
              </span>
            </span>
          </FileLink>
        </li>
      ))}
    </ul>
  );
}


import "/opt/batiflow/app/apps/web/src/features/files/components/recent-files.griffel.css";